import type { RatingBoard, RatingUnqualifiedDisplay } from '@brush/api-client';
import { describeApiError } from '@brush/api-client';
import { ErrorNotice, ErrorRetry, Pill, Spinner } from '@brush/ui';
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  RefreshCw,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import {
  emptyStandingsNotice,
  exclusionNotice,
  offersProvisionalToggle,
  publishesStandings,
  standingsView,
} from '../../ratings/board';
import { cadenceLine, driftNotices, recalcWarning, startControl } from '../../ratings/recalc';
import type { RecalcWarning } from '../../ratings/recalc';
import {
  useCloseRatingBoard,
  useCurrentUser,
  useRatingDrift,
  useRatingSnapshot,
  useRatingStandings,
  useRecalculateRatingBoard,
  useStartRatingBoard,
} from '../../queries';
import { useRoom } from '../../room';
import { RatingBreakdownPanel } from './RatingBreakdownPanel';
import { ExportControl } from '../ExportControl';

/**
 * One rating board's standings — §9.5, and the whole of what Phase 4 shows.
 *
 * **These are the PUBLISHED standings, read back — the same rule a leaderboard follows.** Phase 4
 * computed on every read because nothing was frozen yet; Phase 5 gave ratings snapshots and this
 * screen serves them, because standings a room is judged on must not move under whoever is looking
 * at them. `POST /compute` still exists and answers what the board *would* say; collapsing the two
 * is how a published standing starts drifting under its reader.
 *
 * **What the header does carry is the anchor** (§2.2), and that is not decoration: a rolling-window
 * rating changes when the calendar changes even though no result changed. It is the single most
 * important operational difference between this screen and a leaderboard's, and a board showing a
 * number with no anchor cannot answer "why did my rating drop overnight".
 *
 * **A row opens §7's breakdown**, one at a time. The whole row is the control and a button inside it
 * carries the semantics — `aria-expanded` on the button, the click handler on the row, and the
 * button's keyboard activation reaching that handler by bubbling rather than by a second one that
 * could disagree. Same arrangement as the points designer's own drill-down.
 */
export function RatingBoardStandings({
  board,
  onBack,
}: {
  board: RatingBoard;
  onBack: () => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');

  // §7. Which player's breakdown is open, if any. One at a time: two expanded panels push the rest
  // of a long board off the screen, and the question each answers is about one person.
  const [openPlayerId, setOpenPlayerId] = useState<string | null>(null);

  const me = useCurrentUser();
  const isAdmin = me.data?.isAdmin ?? false;

  /*
   * TWO QUERIES, AND THE DISTINCTION IS THE WHOLE OF §8 ON THIS SCREEN.
   *
   * `snapshotQ` is what the rating PUBLISHES — the figures an explicit recalculation froze, the
   * anchor they were measured against, and when. `standingsQ` runs the engine and answers what the
   * board WOULD say now; it is read here only for the two exclusion counts and §4.6's display rule,
   * which are properties of the configuration rather than of any particular computation.
   *
   * The table renders the snapshot. A room is judged on what it published, and a table that
   * recomputed on every read would move under whoever was looking at it — which is exactly the
   * disagreement `GET /drift` exists to REPORT rather than to cause.
   *
   * NEITHER IS ASKED FOR A BOARD THAT CANNOT HAVE PUBLISHED ANYTHING.
   *
   * A draft can never have a snapshot — recalculating and closing both refuse one — so asking is
   * asking a question whose answer is already known, and what it produced was an error card
   * underneath the empty state the screen had already worked out for itself. `publishesStandings`
   * carries the rule and the reasoning. The live computation then waits on the published one,
   * because everything that reads it renders inside the "there is a snapshot" branch below — the
   * exclusion counts, §4.6's display rule and the events-in-window line. Run before then it is the
   * engine over the room's whole history, answering into a variable nothing on screen consults.
   */
  const snapshotQ = useRatingSnapshot(board.id, publishesStandings(board.status));

  const snapshot = snapshotQ.data ?? null;
  const hasPublished = snapshot !== null && snapshot.sequenceNumber !== null;

  const standingsQ = useRatingStandings(board.id, undefined, hasPublished);
  const live = standingsQ.data;

  // §4.6's rule is the server's, reported on the response rather than read out of the board's
  // `qualification` document — which is an opaque JSON block on the wire, and parsing it here
  // would be a second reader of a document the engine already read.
  const display = (live?.unqualifiedDisplay ?? 'provisional') as RatingUnqualifiedDisplay;

  const view = useMemo(
    () => standingsView(snapshot?.data ?? [], { showAll, search }, display),
    [snapshot, showAll, search, display],
  );

  /*
   * WHAT IS OPEN IS DERIVED, never cleared by an effect.
   *
   * A search or the provisional toggle can remove the row a panel is attached to, and an orphaned
   * panel — arithmetic about a player who is no longer on screen — is the failure to avoid. Deriving
   * it means there is no moment at which the two disagree, which an effect cannot promise: an effect
   * runs after the render that has already drawn the orphan.
   *
   * It is also why the choice is not thrown away. Type a search, and the panel goes; clear it, and it
   * comes back, because the operator never closed it. Clearing the state instead would discard their
   * choice for a filter they were about to undo.
   */
  const openId = view.rows.some((r) => r.playerId === openPlayerId) ? openPlayerId : null;

  const toggle = (playerId: string) =>
    setOpenPlayerId((current) => (current === playerId ? null : playerId));

  const exclusions = live ? exclusionNotice(live) : null;

  // §2.2's two warnings, which apply to a ROLLING window and only a rolling one — see `recalc.ts`.
  const warning = recalcWarning(board.recalcMode, board.windowType);

  return (
    <div className="p-8">
      <button
        type="button"
        onClick={onBack}
        className="mb-6 flex items-center gap-2 text-sm text-ink-800/60 hover:text-ink-950"
      >
        <ArrowLeft className="size-4" /> Back to ratings
      </button>

      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="font-display text-2xl font-semibold text-ink-950">{board.name}</h1>
        <Pill tone={board.status === 'running' ? 'good' : board.status === 'closed' ? 'neutral' : 'warn'}>
          {board.status === 'running' ? 'Running' : board.status === 'closed' ? 'Closed' : 'Draft'}
        </Pill>
        {/* R (docs/specs/export.md EX5): every row of the latest snapshot, whatever this screen's search, provisional
            toggle or display rule, with Qualified and Shortfall. A platform template (no room) has nothing to export. */}
        <div className="ml-auto self-center">
          <ExportControl
            availability={{ view: 'R', status: board.status, isTemplate: board.roomId === null }}
            target={{ view: 'R', ratingBoardId: board.id }}
            viewLabel={board.name}
            subject={board.name}
          />
        </div>
      </div>
      {board.description ? (
        <p className="mt-1 max-w-2xl text-sm text-ink-800/60">{board.description}</p>
      ) : null}

      <RecalculationPanel
        board={board}
        hasPublished={hasPublished}
        isAdmin={isAdmin}
        warning={warning}
      />

      <section className="mt-6 overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
        <header className="border-b border-ink-800/10 px-6 py-4">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h2 className="font-display font-semibold text-ink-950">Standings</h2>
            {hasPublished ? (
              /*
               * §2.2's anchor, stated. A rating is measured *as of* a date and moves with the
               * calendar on its own, so this is the one fact a reader needs before any of the
               * numbers below mean anything — and it is what makes two different answers from the
               * same board over the same results comprehensible rather than alarming.
               *
               * The anchor and the moment the engine ran are BOTH shown, because they genuinely
               * differ: recomputing an awards-night rating a week later, or backfilling a missed
               * schedule, produces a snapshot whose anchor is not the day it was worked out.
               */
              <p className="num text-xs text-ink-800/60">
                As of {snapshot!.asOfDate}
                {live ? ` · ${live.eventsInWindow} of ${live.eventsInScope} events in the window` : ''}
              </p>
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-ink-800/60">
            {hasPublished ? (
              <>
                Published standings — snapshot {snapshot!.sequenceNumber}, worked out{' '}
                {(snapshot!.computedAt ?? '').replace('T', ' ').replace('Z', ' UTC')}
                {snapshot!.isFinal ? '. Final.' : '.'} They do not change until somebody
                recalculates. Open a player to see how their rating was worked out — which results
                counted, which the cut dropped, and what age took off each one.
              </>
            ) : (
              /* Two arrangements — a draft is sent to Start, which is what it needs; see
                 `emptyStandingsNotice`, where the wording and the reason live together. */
              emptyStandingsNotice(board.status).lead
            )}
          </p>
        </header>

        {snapshotQ.isLoading ? (
          <Spinner label="Loading published standings…" />
        ) : snapshotQ.isError ? (
          <div className="p-6">
            <ErrorRetry
              message={describeApiError(snapshotQ.error) ?? "Couldn't load these standings."}
              onRetry={() => void snapshotQ.refetch()}
            />
          </div>
        ) : !hasPublished ? (
          /*
           * A board with no snapshot has PUBLISHED nothing, which is not the same as nobody having
           * scored — and the two send an operator to different places: one to a button, the other
           * to the results. `emptyStandingsNotice` holds both arrangements and the reason there are
           * two: a DRAFT is sent to Start, since Recalculate refuses one, and the panel above this
           * already says so. Two halves of one screen naming different next actions is how an
           * operator concludes the product is broken.
           */
          <p className="p-8 text-center text-sm leading-relaxed text-ink-800/55">
            {emptyStandingsNotice(board.status).lead}
            <br />
            <span className="text-xs">{emptyStandingsNotice(board.status).detail}</span>
          </p>
        ) : standingsQ.isError ? (
          <div className="p-6">
            {/*
             * The server's own sentence, which is the useful half. Several of the refusals here
             * name the one action that resolves them — a template has to be used in a room, a
             * board with no points system needs one chosen — and a generic "couldn't load" would
             * throw that away.
             */}
            <ErrorRetry
              message={describeApiError(standingsQ.error) ?? "Couldn't compute this rating."}
              onRetry={() => void standingsQ.refetch()}
            />
          </div>
        ) : view.total === 0 ? (
          <p className="p-8 text-center text-sm leading-relaxed text-ink-800/55">
            Nobody has a rating on this board yet.
            <br />
            <span className="text-xs">
              Check which events are in scope for it, and that their results have been entered.
            </span>
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-ink-800/10 px-6 py-3">
              <label className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-800/40" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  /* Name and card id, because an operator reading a payout sheet has one string
                     and does not know which column it belongs to. */
                  placeholder="Search by name or card number"
                  className="w-full rounded-lg border border-ink-800/15 py-2 pl-9 pr-3 text-sm"
                />
              </label>

              {offersProvisionalToggle(display) ? (
                <label className="flex shrink-0 items-center gap-2 text-sm text-ink-800/70">
                  <input
                    type="checkbox"
                    checked={showAll}
                    onChange={(e) => setShowAll(e.target.checked)}
                  />
                  Show provisional players
                </label>
              ) : null}
            </div>

            {/*
             * §9.5's "stating how many rows are hidden". A table showing 283 of 1,095 with no count
             * looks like the whole board, and the operator who acts on it is acting on a third of
             * the room. The two hides are named apart because they send somebody to different
             * controls: one is the toggle beside it, the other is the search box.
             */}
            {view.hiddenByQualification > 0 || view.hiddenBySearch > 0 || exclusions ? (
              <div className="space-y-1 border-b border-ink-800/10 bg-felt-50/40 px-6 py-3 text-xs text-ink-800/70">
                {view.hiddenByQualification > 0 ? (
                  <p>
                    {view.hiddenByQualification} provisional{' '}
                    {view.hiddenByQualification === 1 ? 'player is' : 'players are'} not shown —
                    they have not played enough to be rated yet
                    {display === 'hidden' ? ', and this board is set to leave them out.' : '.'}
                  </p>
                ) : null}

                {view.hiddenBySearch > 0 ? (
                  <p>
                    {view.hiddenBySearch} more {view.hiddenBySearch === 1 ? 'row' : 'rows'} hidden
                    by your search.
                  </p>
                ) : null}

                {/*
                 * §4.3's exclusions, which §9.5 requires on the screen rather than in a payload:
                 * a silent exclusion is how a player disappears and nobody can explain it. The two
                 * counts are different facts and the sentence keeps them apart — see
                 * `exclusionNotice`.
                 */}
                {exclusions ? <p>{exclusions}</p> : null}
              </div>
            ) : null}

            {view.rows.length === 0 ? (
              <p className="p-8 text-center text-sm text-ink-800/55">
                No player on this board matches “{search}”.
              </p>
            ) : (
              <>
                {/*
                 * Two arrangements, the rule the leaderboard sheet already follows: six columns at
                 * `px-6` do not fit the 430px reference phone, and this section is
                 * `overflow-hidden` with no scroller, so the rating — the column the board exists
                 * for — would fall off the right-hand edge with no gesture that could reach it.
                 */}
                <ul className="divide-y divide-ink-800/5 md:hidden print:hidden">
                  {view.rows.map((r) => (
                    <li key={r.playerId}>
                      {/* The whole row, because at this width a separate control beside it would be
                          the smaller of two targets on a screen that is already one column. */}
                      <button
                        type="button"
                        aria-expanded={openId === r.playerId}
                        onClick={() => toggle(r.playerId)}
                        className="flex w-full items-start gap-3 px-4 py-3 text-left"
                      >
                        <RankBadge rank={r.rank} isTied={r.isTied} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium text-ink-950">
                            {r.playerName}
                          </span>
                          <span className="num mt-0.5 block text-xs text-ink-800/60">
                            {r.cardId ?? '—'} · {r.eventsSelected} of {r.eventsEntered} events
                          </span>
                          {r.isQualified ? null : (
                            <Provisional shortfall={r.qualificationShortfall} />
                          )}
                        </span>
                        <span className="num shrink-0 text-sm font-semibold text-ink-950">
                          {r.displayRating}
                        </span>
                        <span aria-hidden className="shrink-0 text-[10px] text-ink-800/40">
                          {openId === r.playerId ? '▾' : '▸'}
                        </span>
                      </button>

                      {openId === r.playerId && snapshot?.sequenceNumber != null ? (
                        <RatingBreakdownPanel
                          boardId={board.id}
                          sequenceNumber={snapshot.sequenceNumber}
                          playerId={r.playerId}
                        />
                      ) : null}
                    </li>
                  ))}
                </ul>

                <table className="hidden w-full text-left text-sm md:table print:table">
                  <thead className="bg-felt-50/50 text-xs uppercase tracking-wide text-ink-800/60">
                    <tr>
                      <th className="px-6 py-3 font-medium">Rank</th>
                      <th className="px-6 py-3 font-medium">Player</th>
                      {/*
                       * §2.1's rename, on the screen. "Counted" rather than "scored" because this
                       * is results that survived selection, which includes a non-cashing entry that
                       * made the cut and excludes a cashing one a best-N cut dropped — a different
                       * number from the leaderboard's homonym for the same player.
                       */}
                      <th className="px-6 py-3 text-right font-medium">Events counted</th>
                      <th className="px-6 py-3 text-right font-medium">Events entered</th>
                      <th className="px-6 py-3 text-right font-medium">Cashes</th>
                      <th className="px-6 py-3 text-right font-medium">Rating</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-800/5">
                    {view.rows.map((r) => (
                      <Fragment key={r.playerId}>
                        <tr
                          // The row is the control, so the whole of it responds — but the button in
                          // the player cell is what carries the semantics, and its keyboard
                          // activation reaches this handler by bubbling rather than by a second one
                          // that could disagree with it.
                          onClick={() => toggle(r.playerId)}
                          className={`cursor-pointer hover:bg-felt-50/40 ${
                            openId === r.playerId ? 'bg-felt-50/40' : ''
                          }`}
                        >
                          <td className="px-6 py-3">
                            <RankBadge rank={r.rank} isTied={r.isTied} />
                          </td>
                          <td className="px-6 py-3">
                            <button
                              type="button"
                              aria-expanded={openId === r.playerId}
                              className="flex items-baseline gap-1.5 text-left"
                            >
                              <span aria-hidden className="text-[10px] text-ink-800/40">
                                {openId === r.playerId ? '▾' : '▸'}
                              </span>
                              <span className="font-medium text-ink-950">{r.playerName}</span>
                            </button>
                            <div className="num text-xs text-ink-800/60">{r.cardId ?? '—'}</div>
                            {r.isQualified ? null : (
                              <Provisional shortfall={r.qualificationShortfall} />
                            )}
                          </td>
                          <td className="num px-6 py-3 text-right text-ink-950">
                            {r.eventsSelected}
                          </td>
                          <td className="num px-6 py-3 text-right text-ink-800/70">
                            {r.eventsEntered}
                          </td>
                          <td className="num px-6 py-3 text-right text-ink-800/70">{r.cashes}</td>
                          <td className="num px-6 py-3 text-right font-semibold text-ink-950">
                            {r.displayRating}
                          </td>
                        </tr>

                        {openId === r.playerId && snapshot?.sequenceNumber != null ? (
                          <tr>
                            <td colSpan={6} className="p-0">
                              <RatingBreakdownPanel
                                boardId={board.id}
                                sequenceNumber={snapshot.sequenceNumber}
                                playerId={r.playerId}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <footer className="border-t border-ink-800/10 px-6 py-3 text-xs text-ink-800/55">
              Showing {view.rows.length} of {view.total} rated{' '}
              {view.total === 1 ? 'player' : 'players'} · {view.qualified} rated,{' '}
              {view.provisional} provisional.
            </footer>
          </>
        )}
      </section>
    </div>
  );
}

/**
 * The computed rank, **never the row index**: tied players share a rank and the next one skips
 * (1, 2, 2, 4), so a row number would be wrong even before a filter was applied — and §9.5 is
 * explicit that a filter or a search must never renumber one. One component because both
 * arrangements draw it, and a tie marker present in one and missing from the other would be
 * invisible until somebody compared the two widths.
 */
function RankBadge({ rank, isTied }: { rank: number; isTied: boolean }) {
  return (
    <span className="inline-flex shrink-0 items-center">
      <span className="num inline-flex size-7 items-center justify-center rounded-full bg-ink-800/5 text-xs font-semibold text-ink-800/80">
        {rank}
      </span>
      {isTied ? <span className="ml-1 text-[10px] text-ink-800/45">T</span> : null}
    </span>
  );
}

/**
 * §4.6's "with their shortfall stated", §6.5's `3 of 5 events`.
 *
 * The sentence is the **server's**, rendered verbatim, for the reason the points engine's
 * explanations are: two consoles and a portal wording a shortfall independently are three
 * implementations of one rule that nothing tests.
 */
function Provisional({ shortfall }: { shortfall: string | null }) {
  return (
    <div className="mt-0.5 text-xs text-ink-800/55">
      Provisional{shortfall ? ` — ${shortfall}` : ''}
    </div>
  );
}

/**
 * §8's recalculation controls and §8.1's **two-dimensional** drift indicator.
 *
 * <b>The two drift dimensions are two notices, never one.</b> Data drift means a result changed
 * since the snapshot — a corrected payout, a deal flag, two player records merged. Anchor drift
 * means the rolling window has moved underneath standings nobody has touched, which has no analogue
 * in leaderboards: a rating goes out of date on its own. They are surfaced apart because the
 * operator's response differs — one is "go and check the import", the other is "press
 * Recalculate" — and a single "out of date" throws away the only thing that makes either of them
 * actionable. `driftNotices` is where that rule lives, with tests.
 *
 * <b>Drift is asked for only by an administrator.</b> The endpoint is admin-only and §8.1 asks for
 * it as an admin alert, so firing it as an operator would put a permanent 403 notice on the screen,
 * which reads as a broken app rather than as a report they do not have.
 */
function RecalculationPanel({
  board,
  hasPublished,
  isAdmin,
  warning,
}: {
  board: RatingBoard;

  /**
   * Whether this board has published anything — the only thing this panel ever asked the snapshot,
   * so it takes the answer rather than the record. Drift is a comparison against a published
   * standing, so there is nothing to ask about until there is one.
   */
  hasPublished: boolean;
  isAdmin: boolean;
  warning: RecalcWarning | null;
}) {
  const { roomId } = useRoom();

  const recalculate = useRecalculateRatingBoard(roomId);
  const close = useCloseRatingBoard(roomId);
  const startBoard = useStartRatingBoard(roomId);
  const driftQ = useRatingDrift(board.id, isAdmin && hasPublished);

  const [reason, setReason] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmStart, setConfirmStart] = useState(false);

  // §2.4's Draft → Running: whether to offer it at all, and why it cannot be pressed if so.
  const start = startControl(board);

  const closed = board.status === 'closed';
  const draft = board.status === 'draft';

  // §8.2: a closed board moves only for an administrator, and only with a reason. The box appears
  // before the attempt rather than after the refusal — discovering the requirement by being
  // refused makes the audit trail feel like an obstacle, when it is the point of the operation.
  const canRecalculate = !draft && (!closed || (isAdmin && reason.trim().length > 0));

  const notices = driftQ.data ? driftNotices(driftQ.data) : [];

  return (
    <section className="mt-6 rounded-xl border border-ink-800/10 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="font-display font-semibold text-ink-950">Recalculation</h2>

        {/* Locked rule 4's distinction: a schedule this room chose reads differently from the
            platform's, and a screen showing only the resolved answer would present an inherited
            cadence as a choice this room made. See `cadenceLine`. */}
        <p className="flex items-center gap-1.5 text-xs text-ink-800/60">
          <CalendarClock className="size-3.5 shrink-0" />
          {cadenceLine(board)}
        </p>
      </div>

      {/*
       * §2.2's warnings, and they are WARNINGS: freezing a rating for an awards night is
       * legitimate, so nothing here refuses or disables anything. The sentence says what it costs.
       */}
      {warning ? (
        <p className="mt-3 border border-chip-500/40 bg-chip-500/5 p-3 text-[11.5px] leading-relaxed text-ink-800/80">
          <AlertTriangle className="mr-1.5 inline size-3.5 -translate-y-px text-chip-500" />
          {warning.message}
        </p>
      ) : null}

      {draft ? (
        <p className="mt-3 text-[11.5px] leading-relaxed text-ink-800/65">
          This rating is a draft, so nothing has been pinned and there is nothing to publish yet.
          Start it to pin its points system — that is what makes its standings reproducible.
        </p>
      ) : null}

      {/*
       * §13.3's refusal, before the round trip rather than after it — and never a bare disabled
       * button, which is the leaderboard's own rule for its Start and the half this walk keeps
       * finding missing. `startControl` carries the wording and where to go.
       */}
      {start.kind === 'blocked' ? (
        <p className="mt-3 text-[11.5px] leading-relaxed text-ink-800/65">{start.reason}</p>
      ) : null}

      {confirmStart ? (
        <p className="mt-3 border border-gold-500/40 bg-gold-500/5 p-3 text-[11.5px] leading-relaxed text-ink-800/80">
          {/* §2.4: the pin never changes afterwards. Two clicks, and the second states the
              consequence — the same shape Close beside it uses, and room-web's other irreversible
              actions. The builder already says this; it is said here because here is where it
              happens. */}
          Starting pins the points system version this rating is carrying, and the pin never changes
          afterwards — every future recalculation scores against it. The window, the periods and the
          minimum stay editable.
        </p>
      ) : null}

      {closed && isAdmin ? (
        <label className="mt-3 block">
          <span className="mb-1 block text-xs text-ink-800/60">
            Why are you recalculating a closed rating?
          </span>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Event 12 was imported with the wrong deal flag"
            className="w-full rounded-lg border border-ink-800/15 px-3 py-2 text-sm"
          />
          <span className="mt-1 block text-[10.5px] text-ink-800/45">
            Recorded on the snapshot. These standings have already been published, and every
            previous snapshot is kept.
          </span>
        </label>
      ) : null}

      {closed && !isAdmin ? (
        <p className="mt-3 text-[11.5px] leading-relaxed text-ink-800/65">
          Only a platform administrator can recalculate a closed rating, and only with a reason.
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {/*
         * §2.4's Start, WHICH NOTHING RENDERED. The route and `useStartRatingBoard` shipped in
         * Phase 4 and no component ever called the hook, so a Draft could not be started, no rating
         * could pin a system, and no rating could publish standings — while this panel's own
         * sentence three lines up told the operator to start it.
         *
         * It REPLACES Recalculate on a draft rather than joining it. A draft can never recalculate
         * until it starts, so a disabled Recalculate beside a live Start is a dead control next to
         * the real one — the arrangement this walk has already produced two bugs from.
         */}
        {start.kind !== 'absent' ? (
          <button
            type="button"
            disabled={start.kind !== 'ready' || startBoard.isPending}
            onClick={() =>
              confirmStart
                ? startBoard.mutate(board.id, { onSuccess: () => setConfirmStart(false) })
                : setConfirmStart(true)
            }
            className="rounded-lg bg-felt-700 px-3 py-2 text-sm font-medium text-white transition hover:bg-felt-900 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {startBoard.isPending
              ? 'Starting…'
              : confirmStart
                ? 'Confirm — pin the points system'
                : 'Start rating'}
          </button>
        ) : null}

        {draft ? null : (
        <button
          type="button"
          disabled={!canRecalculate || recalculate.isPending}
          onClick={() =>
            recalculate.mutate(
              { id: board.id, reason: closed ? reason.trim() : null },
              { onSuccess: () => setReason('') },
            )
          }
          className="inline-flex items-center gap-2 rounded-lg border border-ink-800/15 px-3 py-2 text-sm text-ink-800 hover:bg-felt-50 disabled:opacity-40"
        >
          <RefreshCw className={recalculate.isPending ? 'size-4 animate-spin' : 'size-4'} />
          {recalculate.isPending ? 'Recalculating…' : 'Recalculate now'}
        </button>
        )}

        {!closed && !draft ? (
          <button
            type="button"
            disabled={close.isPending}
            onClick={() => (confirmClose ? close.mutate(board.id) : setConfirmClose(true))}
            className="rounded-lg border border-gold-500/50 px-3 py-2 text-sm text-ink-900 hover:bg-gold-500/10 disabled:opacity-40"
          >
            {close.isPending
              ? 'Closing…'
              : confirmClose
                ? 'Confirm — write the final snapshot'
                : 'Close rating'}
          </button>
        ) : null}

        {confirmClose && !closed ? (
          <span className="text-[11px] leading-relaxed text-ink-800/60">
            Closing writes the final snapshot and stops anything recalculating this rating
            automatically. After that only an administrator can move it, and only with a reason.
          </span>
        ) : null}
      </div>

      <ErrorNotice
        message={
          describeApiError(startBoard.error) ??
          describeApiError(recalculate.error) ??
          describeApiError(close.error)
        }
        className="mt-3"
      />

      {/*
       * ONE NOTICE PER DIMENSION. A merged indicator would pass for correct — it would say the
       * standings are out of date, which is true — while losing the distinction that tells an
       * operator whether there is an import to go and look at.
       */}
      {notices.map((notice) => (
        <div
          key={notice.kind}
          className={
            // Only DATA is tinted as a warning. The calendar moving is what a rolling rating does,
            // and a setting changing is something the operator did on purpose — neither is a fault,
            // and colouring them as one is how the one that IS a fault stops being noticed.
            notice.kind === 'data'
              ? 'mt-3 border border-chip-500/40 bg-chip-500/5 p-3'
              : 'mt-3 border border-ink-800/15 bg-felt-50/50 p-3'
          }
        >
          <span className="flex items-center gap-1.5 text-xs font-semibold text-ink-950">
            {notice.kind === 'data' ? (
              <>
                <AlertTriangle className="size-3.5 text-chip-500" />
                A result behind this rating has changed
              </>
            ) : notice.kind === 'configuration' ? (
              <>
                {/* §8.1's third dimension. A pencil rather than a warning: this heading used to say
                    "a result behind this rating has changed" about an operator editing their own
                    board, which sent them to audit an import that was fine. */}
                <SlidersHorizontal className="size-3.5 text-ink-800/60" />
                This rating’s settings have changed
              </>
            ) : (
              <>
                {/* Not a warning icon, deliberately. Nothing is wrong: the calendar moved, which
                    is what a rolling rating does, and the fix is one button. */}
                <CalendarClock className="size-3.5 text-ink-800/60" />
                These standings are behind the calendar
              </>
            )}
          </span>
          <p className="mt-1 text-[11.5px] leading-relaxed text-ink-800/70">{notice.message}</p>
        </div>
      ))}
    </section>
  );
}
